import { format as fnsFormat, parseISO, subDays, startOfMonth } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

/** The only display timezone in the admin panel. */
export const TZ = 'Asia/Tbilisi' as const;

/**
 * Format a Date or ISO string in Asia/Tbilisi.
 * Use this anywhere you render a booking date/time.
 */
export function formatLocal(value: Date | string, fmt: string): string {
  const d = typeof value === 'string' ? parseISO(value) : value;
  return formatInTimeZone(d, TZ, fmt);
}

/**
 * Today's date in Asia/Tbilisi as YYYY-MM-DD (the format the API expects
 * for /admin/bookings/{date_str}).
 */
export function todayLocal(): string {
  return formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
}

/**
 * Current minute-of-day in Asia/Tbilisi (0–1439). Used to position the
 * "now" line on the bookings grid.
 */
export function nowMinutesLocal(): number {
  const [h, m] = formatInTimeZone(new Date(), TZ, 'HH:mm').split(':').map(Number);
  return h * 60 + m;
}

/**
 * Render a minute-of-day count as a **canonical** "HH:MM" string.
 *
 * This is the internal/wire form used for `<TimeSelect>` option values, state,
 * and the times sent back to `prospin-api` — it is deliberately locale-independent
 * so comparisons and round-trips are stable. For anything a user reads, format it
 * for display with `formatClock`. Values are not wrapped modulo a day, so 1440
 * renders as "24:00" (the API's end-of-day boundary) rather than "00:00".
 */
export function minutesToHm(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// API time-of-day strings are **venue-local (Asia/Tbilisi) wall-clock**, with no
// timezone — never parse them as instants. The pattern is deliberately anchored:
// a lenient prefix match would accept "2:30 PM" and return "02:30", turning an
// afternoon time into a morning one. Non-conforming input is returned unchanged
// (with a DEV warning) rather than mangled — plausible-looking wrong output is
// worse than obviously-wrong output.
const TIME_OF_DAY_RE = /^(\d{1,2}):(\d{2})(:\d{2})?$/;

interface TimeOfDay {
  h: number;
  min: number;
  hh: string; // zero-padded hour, e.g. "09"
  mm: string; // minutes, e.g. "05"
}

function parseTimeOfDay(value: string, who: string): TimeOfDay | null {
  const m = TIME_OF_DAY_RE.exec(value);
  if (m) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    // The regex only checks *shape*, so it still admits out-of-range fields like
    // "12:75" or "25:15". Range-check before use: a Date would silently roll
    // "12:75" over to "13:15" — exactly the plausible-looking wrong output this
    // helper exists to prevent. Valid times are 00:00–23:59 plus the API's
    // 24:00 end-of-day boundary (hour 24 only at minute 0).
    const inRange = min < 60 && h <= 24 && !(h === 24 && min > 0);
    if (inRange) {
      return { h, min, hh: m[1].padStart(2, '0'), mm: m[2] };
    }
  }
  if (import.meta.env.DEV) {
    console.warn(`${who}: expected a valid "HH:MM"/"HH:MM:SS", got ${JSON.stringify(value)}`);
  }
  return null;
}

/**
 * Normalize an API time-of-day string to the **canonical** "HH:MM" form.
 *
 * Accepts "HH:MM" or "HH:MM:SS" and drops the seconds. This produces the
 * locale-independent value used for state seeding, dirty-checks, and
 * `<TimeSelect>` option values; render it for the user with `formatClock`, which
 * honours the device's 12-/24-hour preference.
 */
export function formatTimeOfDay(value: string | null | undefined): string {
  if (!value) return '';
  const t = parseTimeOfDay(value, 'formatTimeOfDay');
  if (!t) return value;
  return `${t.hh}:${t.mm}`;
}

/**
 * Minute-of-day (0–1440) for an API time-of-day string, or `null` when it
 * doesn't parse. The inverse of `minutesToHm`.
 *
 * Goes through the same `parseTimeOfDay` as the formatters, so callers doing
 * arithmetic on API times (durations, offsets) inherit the identical range
 * rules — a second, looser parser would accept "2:30 PM" as 02:30 or roll
 * "12:75" over to 13:15, which is precisely what that helper exists to stop.
 */
export function minutesOfDay(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = parseTimeOfDay(value, 'minutesOfDay');
  if (!t) return null;
  return t.h * 60 + t.min;
}

/**
 * Format a venue-local wall-clock time **for display**, honouring the device's
 * locale hour cycle: a 12-hour device gets "5:15 PM", a 24-hour device gets
 * "17:15". Use this for every user-facing render of an API time-of-day
 * (`start_time` / `end_time`) or a `minutesToHm` value — never render the
 * canonical "HH:MM" directly, which drops the AM/PM a 12-hour device expects.
 *
 * The input is a venue-local wall-clock string with no timezone. We build a UTC
 * `Date` from its hour/minute and format it with `timeZone: 'UTC'`, so the
 * rendered clock is exactly the input hour/minute regardless of the host zone's
 * DST rules for the anchor date.
 *
 * The API's end-of-day boundary ("24:00") is rendered as the locale's midnight
 * in a 12-hour cycle (e.g. "12:00 AM") so it matches the notation of the other
 * options in the same dropdown, and kept as the literal "24:00" in a 24-hour
 * cycle where "00:00" would collide with start-of-day.
 */
export function formatClock(value: string | null | undefined): string {
  if (!value) return '';
  const t = parseTimeOfDay(value, 'formatClock');
  if (!t) return value;
  // Only the 24:00 boundary reaches here with hour 24 (parseTimeOfDay rejects
  // any other hour >= 24, and guarantees minute 0 at hour 24 — so no minutes
  // are dropped). Anchor midnight explicitly rather than passing 24 to Date.UTC.
  if (t.h === 24) {
    return usesTwelveHourClock()
      ? clockFormat().format(new Date(Date.UTC(2000, 0, 1, 0, 0)))
      : `${t.hh}:${t.mm}`;
  }
  return clockFormat().format(new Date(Date.UTC(2000, 0, 1, t.h, t.min)));
}

/**
 * Format an instant (Date or ISO string) as a **display** clock in Asia/Tbilisi,
 * honouring the device's 12-/24-hour locale preference. Use for `TIMESTAMPTZ`
 * values like `created_at`; pair with `formatLocal(value, 'yyyy-MM-dd')` for the
 * date part.
 */
export function formatVenueTime(value: Date | string): string {
  const d = typeof value === 'string' ? parseISO(value) : value;
  return venueClockFormat().format(d);
}

// Locale is resolved from the device (undefined locale = runtime default), which
// is where the OS/browser 12- vs 24-hour preference surfaces. Formatters are
// memoized; the device locale does not change within a session.
// Hour style follows the resolved cycle: 12-hour locales get a numeric hour so
// it reads idiomatically ("5:15 PM", not "05:15 PM"), while 24-hour locales keep
// a zero-padded hour so columns stay aligned in the bookings grid ("09:05", not
// "9:05"). Asking for one style in both worlds gets one of them wrong.
function usesTwelveHourClock(): boolean {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12 === true;
}

let _clockFormat: Intl.DateTimeFormat | undefined;
function clockFormat(): Intl.DateTimeFormat {
  // timeZone: 'UTC' pairs with the Date.UTC(...) anchor in formatClock so the
  // rendered clock is the literal input hour/minute — no host-zone DST shift.
  return (_clockFormat ??= new Intl.DateTimeFormat(undefined, {
    hour: usesTwelveHourClock() ? 'numeric' : '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }));
}

let _venueClockFormat: Intl.DateTimeFormat | undefined;
function venueClockFormat(): Intl.DateTimeFormat {
  return (_venueClockFormat ??= new Intl.DateTimeFormat(undefined, {
    hour: usesTwelveHourClock() ? 'numeric' : '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  }));
}

/** Convert a UTC Date to its Tbilisi-local equivalent. Rarely needed. */
export function toLocal(d: Date): Date {
  return toZonedTime(d, TZ);
}

/** Re-export for places that already have a Date in local time. */
export { fnsFormat as formatDate };

export type StatsWindow = '7d' | '30d' | '90d' | 'month';

/**
 * Inclusive {start, end} YYYY-MM-DD date strings for a stats window, anchored
 * on today in Asia/Tbilisi. Pure date arithmetic — no timezone conversion, so
 * the date parts never shift.
 */
export function periodRange(window: StatsWindow): { start: string; end: string } {
  const end = todayLocal();
  const today = parseISO(end);
  let start: Date;
  if (window === '7d') start = subDays(today, 6);
  else if (window === '30d') start = subDays(today, 29);
  else if (window === '90d') start = subDays(today, 89);
  else start = startOfMonth(today);
  return { start: fnsFormat(start, 'yyyy-MM-dd'), end };
}
